// hooks/auth/useAuthCredentials.ts
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAuthStatus,
  selectAuthError,
  selectFieldErrors,
} from '@/store/selectors/auth';
import { credentialVerificationThunk } from '@/store/thunks/auth/credentialVerificationThunk';
import { clearLoginError } from '@/store/slices/auth/authSlice';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';

/**
 * Hook for /login route
 * Handles username/password credential submission and navigation to 2FA selection
 *
 * @returns {Object} Hook state and handlers
 * @returns {Object} return.state - Current authentication state
 * @returns {boolean} return.state.isLoading - Loading indicator during submission
 * @returns {string} [return.state.error] - Global error message if authentication fails
 * @returns {Object} return.state.fieldErrors - Field-level validation errors (username, password)
 * @returns {Object} return.handlers - Action handlers
 * @returns {Function} return.handlers.submit - Submit credentials (username, password)
 *
 * @example
 * const { state, handlers } = useAuthCredentials();
 *
 * return (
 *   <LoginForm
 *     onSubmit={handlers.submit}
 *     loading={state.isLoading}
 *     error={state.error}
 *     fieldErrors={state.fieldErrors}
 *   />
 * );
 */
export function useAuthCredentials() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLocallySubmitting, setIsLocallySubmitting] = useState(false);
  const { handlers: loaderHandlers } = useGlobalLoader();

  const authStatus = useAppSelector(selectAuthStatus);
  const error = useAppSelector(selectAuthError);
  const fieldErrors = useAppSelector(selectFieldErrors);
  const isLoading = authStatus === 'loading' || isLocallySubmitting;

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  const submit = async(credentials: { username: string; password: string }) => {
    setIsLocallySubmitting(true);
    loaderHandlers.showLoader();
    try {
      const action = await dispatch(credentialVerificationThunk(credentials));
      if (credentialVerificationThunk.fulfilled.match(action)) {
        await navigate({ to: '/login/2fa/select' });
      }
    } finally {
      loaderHandlers.hideLoader();
      setIsLocallySubmitting(false);
    }
  };

  return {
    state: { isLoading, error, fieldErrors },
    handlers: { submit },
  };
}
